import React, { useEffect } from "react";
import Text from "../../../../shared-components/css-modules/toyota/atoms/Text";
import HighlightsNavigation from "./HighlightsNavigation";
import SectionWrapper from "./sections/SectionWrapper";
import { CarConfigHighlightSection } from "../../../utils/types/SectionTypes";
import ContentSection from "./sections/ContentSection";
import styles from "./Highlights.module.scss";
import { useAphStepTracking } from "../../../hooks/useAphStepTracking";
import { useCarConfigSelector } from "../../../redux/store";
import { numberIsEven } from "../../../utils/typedUtils";
import { getVisualForGradeImage } from "../../../../../../common-deprecated/settings/utils/ccisSettingUtils";
import useSectionIntersectionObserver from "../../../hooks/useSectionIntersectionObserver";
import { CarConfigStep } from "../../../../../../common-deprecated/features/apheleia/utils/carConfigNavigationUtils";
import { CarConfigLabel } from "../../../utils/labelUtils";
import { HIGHLIGHTS_STEP, TAB_HIGHLIGHTS } from "../../../utils/dataTestIDs";

const Highlights: React.FC = () => {
    const commonSettings = useCarConfigSelector((state) => state.commonSettings);
    const car = useCarConfigSelector((state) => state.car);
    const gradeFeatures = useCarConfigSelector((state) =>
        state.grades.items.find((grade) => grade.id === state.car.grade.id),
    )?.gradeFeatures.filter(({ valid }) => valid);

    const allSections = gradeFeatures?.map((gradeFeature) => gradeFeature.id) || [];
    // Creates a Mappable collection of all the sections and their Refs
    const sectionRefs = new Map(
        [CarConfigHighlightSection.SectionHeader, ...(allSections as CarConfigHighlightSection[])].map((section) => [
            section,
            React.createRef<HTMLElement>(),
        ]),
    );

    useSectionIntersectionObserver<CarConfigHighlightSection>(sectionRefs);

    const trackStep = useAphStepTracking();

    useEffect(() => {
        trackStep(CarConfigStep.Highlights);

        // UseEffect only needs to run once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!gradeFeatures) return null;

    const headerImageUrl = getVisualForGradeImage(
        commonSettings,
        car.model.id,
        car.grade.id,
        car.body.id,
        {
            type: "webp",
            padding: 700,
            bgImage: "hd-day-white",
            imageQuality: 100,
            srcSet: [
                { width: 1700, height: 800, padding: 700 },
                { width: 1000, height: 470, padding: 350 },
                { width: 500, height: 235, padding: 175 },
            ],
        },
        14,
    );

    return (
        <div className={styles.wrapper} id={TAB_HIGHLIGHTS}>
            <SectionWrapper
                sectionId={CarConfigHighlightSection.SectionHeader}
                sectionRef={sectionRefs.get(CarConfigHighlightSection.SectionHeader)}
                wrap={false}
            >
                <header className={styles.header}>
                    <img
                        {...headerImageUrl}
                        width="1700"
                        height="800"
                        sizes="(max-width: 500px) 500px, (max-width: 1000px) 1000px, 1700px"
                        className={styles.hero}
                        loading="eager"
                        alt=""
                        role="presentation"
                    />
                    <Text tag="h1" variant="apheleia-h2" className={styles.title}>
                        <CarConfigLabel
                            useHTML
                            label="carConfigHighlightsIconicFeatures"
                            params={{ model: car.model.webtitleSectionName }}
                        />
                    </Text>
                </header>
            </SectionWrapper>

            <main data-testid={HIGHLIGHTS_STEP}>
                {gradeFeatures.map((gradeFeature, index) => (
                    <SectionWrapper
                        sectionId={gradeFeature.id}
                        sectionRef={sectionRefs.get(gradeFeature.id as CarConfigHighlightSection)}
                        key={gradeFeature.id}
                    >
                        <ContentSection
                            gradeFeature={gradeFeature}
                            contentPosition={numberIsEven(index) ? "end" : "start"}
                        />
                    </SectionWrapper>
                ))}
            </main>
            <HighlightsNavigation />
        </div>
    );
};

export default Highlights;
